import React from 'react'
import { ListGroup } from 'react-bootstrap'
import Button from '@components/common/Button/Button.component'
import TagBadge from '@components/common/TagBadge/TagBadge.component'
import type { NoteContextType } from '@components/common/TagBadge/TagBadge.component'
import { getPreviewContentText } from '@components/common/NoteTextEditor/NoteTextEditor.component'
import AddNoteFilter from '@components/layout/Modals/AddNote/AddNoteFilter'
import { INote } from '@dataTypes/note.type'
import {
  getContextItemDisplayName,
  type NoteFilterContextLookup,
} from '@utils/noteFilter.util'
import { getNoteSidebarAuthorLabel } from '@utils/noteSidebarAuthor.util'
import {
  formatNoteTitleWithVersion,
  hasMultipleVersionsInChain,
} from '@utils/noteVersion.util'
import { format } from 'date-fns'
import { getNoteContextItemId } from './noteEditor.helpers'
import type { NoteSidebarFilters, UnsavedModalType } from './noteEditor.types'

type NoteEditorContextSidebarProps = {
  claimContextNotesReadOnly: boolean
  hasContextId: boolean
  contextId?: string | null
  activeNote: INote | null
  activeMode: 'view' | 'edit'
  hasUnsavedChanges: boolean
  onProceedToNewNote: () => void
  setUnsavedModalType: (value: UnsavedModalType) => void
  contextType?: NoteContextType
  isContactContext: boolean | string
  sidebarSearchQuery: string
  setSidebarSearchQuery: (value: string) => void
  onApplyFilters: (filters: NoteSidebarFilters) => void
  contextNotes: INote[]
  contextLookup: NoteFilterContextLookup
  sidebarAppliedFilters: NoteSidebarFilters
  contextNotesFetched: boolean
  shouldDelaySidebarListRender: boolean
  sortedSidebarFilteredNotes: INote[]
  sidebarFilteredNotes: INote[]
  displayedNote: INote | null
  hasContextNotesOwnership: boolean
  onEditNote?: (note?: INote | null) => void
  mode?: 'edit' | 'view'
  onViewNote?: (note: INote) => void
  onBeforeOpenNote: () => void
  setPendingSidebarNote: (note: INote | null) => void
  setActiveNote: (note: INote | null) => void
  setActiveMode: (mode: 'view' | 'edit') => void
}

const NoteEditorContextSidebar = ({
  claimContextNotesReadOnly,
  hasContextId,
  contextId,
  activeNote,
  activeMode,
  hasUnsavedChanges,
  onProceedToNewNote,
  setUnsavedModalType,
  contextType,
  isContactContext,
  sidebarSearchQuery,
  setSidebarSearchQuery,
  onApplyFilters,
  contextNotes,
  contextLookup,
  sidebarAppliedFilters,
  contextNotesFetched,
  shouldDelaySidebarListRender,
  sortedSidebarFilteredNotes,
  sidebarFilteredNotes,
  displayedNote,
  hasContextNotesOwnership,
  onEditNote,
  mode,
  onViewNote,
  onBeforeOpenNote,
  setPendingSidebarNote,
  setActiveNote,
  setActiveMode,
}: NoteEditorContextSidebarProps): JSX.Element => {
  const canOpenNote = Boolean(
    hasContextNotesOwnership || onEditNote || (mode === 'view' && onViewNote),
  )
  const openNote = (item: INote): void => {
    setTimeout(() => {
      if (hasUnsavedChanges) {
        setPendingSidebarNote(item)
        setUnsavedModalType('openSidebarNote')
      } else if (hasContextNotesOwnership) {
        setActiveNote(item)
        setActiveMode('view')
      } else if (mode === 'view' && onViewNote) {
        onViewNote(item)
      } else {
        onEditNote?.(item)
      }
    }, 0)
  }
  return (
    <div className="add-note-context-sidebar">
      <div className="add-note-context-sidebar-header">
        <Button
          size="md"
          className="button-primary add-note-context-sidebar-new-btn"
          disabled={
            claimContextNotesReadOnly ||
            (hasContextId && (!contextId || !contextId.trim())) ||
            (!activeNote && activeMode === 'edit')
          }
          onClick={() => {
            if (claimContextNotesReadOnly) return
            if (hasUnsavedChanges) setUnsavedModalType('newNote')
            else onProceedToNewNote()
          }}
          title={
            claimContextNotesReadOnly
              ? 'Notes can only be added when the claim is rejected.'
              : hasContextId && contextId && contextId.trim()
                ? `Add note to current context (${contextType ?? 'context'})`
                : hasContextId
                  ? 'Select a context to add a note'
                  : 'New note'
          }
        >
          <span>New note</span>
        </Button>
      </div>
      <div className="add-note-context-sidebar-filter d-flex w-100">
        <AddNoteFilter
          key={`sidebar-filter-${contextId ?? (isContactContext ? 'contact' : 'all')}`}
          searchTerm={sidebarSearchQuery}
          onSearchChange={setSidebarSearchQuery}
          onApply={onApplyFilters}
          refList={
            isContactContext
              ? []
              : contextNotes
                  .map((n) => ({
                    context_type: n.context_type ?? '',
                    context_id: getNoteContextItemId(n) || null,
                    context_name: n.context_display_name ?? undefined,
                    context_details: n.context_details ?? null,
                    refName: getContextItemDisplayName(
                      n.context_type ?? '',
                      n.context_id ?? null,
                      contextLookup,
                    ),
                  }))
                  .filter(
                    (item, index, self) =>
                      index ===
                      self.findIndex((x) => x.context_id === item.context_id),
                  )
          }
          initialFilter={sidebarAppliedFilters}
        />
      </div>
      <div className="add-note-context-sidebar-list">
        <ListGroup className="add-note-context-sidebar-listgroup">
          {!contextNotesFetched || shouldDelaySidebarListRender ? (
            <div
              className="add-note-context-sidebar-loading-blank"
              aria-hidden
            />
          ) : sortedSidebarFilteredNotes.length > 0 ? (
            sortedSidebarFilteredNotes.map((noteItem) => (
              <div
                key={noteItem.note_id}
                className={`add-note-context-sidebar-item${
                  displayedNote?.note_id &&
                  noteItem.note_id === displayedNote.note_id
                    ? ' add-note-context-sidebar-item-active'
                    : ''
                }`}
                role={canOpenNote ? 'button' : undefined}
                tabIndex={canOpenNote ? 0 : undefined}
                onClick={(e) => {
                  onBeforeOpenNote()
                  ;(e.currentTarget as HTMLElement)?.focus?.()
                  openNote(noteItem)
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    onBeforeOpenNote()
                    ;(e.currentTarget as HTMLElement)?.focus?.()
                    openNote(noteItem)
                  }
                }}
              >
                <div className="anm-li-title">
                  <div className="anm-title">
                    {hasMultipleVersionsInChain(noteItem, sidebarFilteredNotes)
                      ? formatNoteTitleWithVersion({
                          title: noteItem.title ?? '',
                          version: noteItem.version,
                        })
                      : noteItem.title}
                  </div>
                  <div className="anm-date">
                    {format(
                      new Date(noteItem.date_time_updated_at ?? ''),
                      'MM/dd/yyyy - hh:mm a',
                    )}
                  </div>
                </div>
                <div className="anm-content">
                  {getPreviewContentText(noteItem.body ?? '')}
                </div>
                <div className="anm-footer">
                  <div className="anm-id">
                    {getNoteSidebarAuthorLabel(noteItem)}
                  </div>
                  <div className="d-flex flex-row gap-2">
                    <TagBadge tag={noteItem.context_type ?? ''} />
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="add-note-context-sidebar-empty">
              No notes found.
            </div>
          )}
        </ListGroup>
      </div>
    </div>
  )
}

export default NoteEditorContextSidebar
